import { z } from 'zod';

import { t } from '@/lib/utils';

export const ADDRESS_TYPE = {
    company: 'company',
    individual: 'individual',
} as const;

export const SHIPPING_FIELD_MAX_LENGTH = {
    companyName: 100,
    recipient: 100,
    address: 150,
    postCode: 20,
    city: 50,
} as const;

export const ShippingSchema = () => {
    // Resolve translations while the schema is built (during render), because `t` uses a hook
    // internally and cannot be called later from within the `superRefine` validation callback.
    const atLeastOneLetter = t('general.atLeastOneLetter');
    const selectOneCountry = t('general.selectOneCountry');

    return z
        .object({
            address: z.string().min(1, atLeastOneLetter),
            address2: z.optional(z.string()),
            companyName: z.optional(z.string()),
            postCode: z.string().min(1, atLeastOneLetter),
            city: z.string().min(1, atLeastOneLetter),
            country: z.string().min(1, selectOneCountry),
            addressee: z.optional(z.string()),
            addressType: z.optional(z.enum([ADDRESS_TYPE.company, ADDRESS_TYPE.individual])),
        })
        .superRefine((shipping, ctx) => {
            // The company name is only required when the "company" shipping label is selected.
            if (shipping.addressType === ADDRESS_TYPE.company && !shipping.companyName?.trim()) {
                ctx.addIssue({
                    code: 'custom',
                    message: atLeastOneLetter,
                    path: ['companyName'],
                    input: shipping.companyName,
                });
            }
            // The addressee is required when the "individual" shipping label is selected.
            if (shipping.addressType === ADDRESS_TYPE.individual && !shipping.addressee?.trim()) {
                ctx.addIssue({
                    code: 'custom',
                    message: atLeastOneLetter,
                    path: ['addressee'],
                    input: shipping.addressee,
                });
            }
        });
};

export type ShippingSchemaType = z.infer<ReturnType<typeof ShippingSchema>>;
